import { useEffect } from 'react'
import { BrowserRouter, Routes, Route, Navigate, useParams, useLocation, createRoutesFromElements } from 'react-router-dom'
import { BRAND } from '@claritio/frontend/lib/brand'
import { landingPath } from '@claritio/frontend/lib/recentOrgs'
import { AuthProvider, RequireOrg, RequireAdmin, RequirePlatformAdmin, RequireEmployee, RequireAuth } from '@claritio/frontend/lib/auth'
import { TokenInterviewLayout, AssessmentInterviewLayout } from '@claritio/frontend/lib/interviewContext'
import AppShell from '@claritio/frontend/components/AppShell'
import RespondentShell from '@claritio/frontend/components/RespondentShell'
import FloatingLanguageToggle from '@claritio/frontend/components/FloatingLanguageToggle'
import BootScreen from '@claritio/frontend/components/BootScreen'
import PageBoundary from '@claritio/frontend/components/PageBoundary'
import { lazyPage, registerRoutes } from '@claritio/frontend/lib/lazyPage'
import Login from '@claritio/frontend/pages/Login'
import ComingSoon, { SoonPage, NoAccessPage } from '@claritio/frontend/pages/ComingSoon'
import { RequireSponsorProcesses } from '@claritio/frontend/pages/ProcessIntelligence/sponsor/SponsorPage'

const InterviewWelcome = lazyPage(() => import('@claritio/frontend/pages/interview/Welcome'))
const InterviewChat = lazyPage(() => import('@claritio/frontend/pages/interview/Chat'))
const InterviewDone = lazyPage(() => import('@claritio/frontend/pages/interview/Done'))
const SurveyForm = lazyPage(() => import('@claritio/frontend/pages/interview/SurveyForm'))
const SurveyThanks = lazyPage(() => import('@claritio/frontend/pages/interview/SurveyThanks'))
const PublicReport = lazyPage(() => import('@claritio/frontend/pages/PublicReport'))
const PublicStatus = lazyPage(() => import('@claritio/frontend/pages/PublicStatus'))
const FollowUpReply = lazyPage(() => import('@claritio/frontend/pages/interview/FollowUpReply'))
const NoAccess = lazyPage(() => import('@claritio/frontend/pages/NoAccess'))
const ForgotPassword = lazyPage(() => import('@claritio/frontend/pages/ForgotPassword'))
const ResetPassword = lazyPage(() => import('@claritio/frontend/pages/ResetPassword'))
const OAuthCallback = lazyPage(() => import('@claritio/frontend/pages/OAuthCallback'))
const Clients = lazyPage(() => import('@claritio/frontend/pages/Clients'))
const ClientPanel = lazyPage(() => import('@claritio/frontend/pages/ClientPanel'))
const Home = lazyPage(() => import('@claritio/frontend/pages/Home'))
const McpUsagePage = lazyPage(() => import('@claritio/frontend/pages/McpUsage/McpUsagePage'))
const SkillsPage = lazyPage(() => import('@claritio/frontend/pages/Skills/SkillsPage'))
const Overview = lazyPage(() => import('@claritio/frontend/pages/Overview'))
const ProfileSettings = lazyPage(() => import('@claritio/frontend/pages/ProfileSettings'))
const AssessmentsList = lazyPage(() => import('@claritio/frontend/pages/AssessmentsList'))
const ProcessList = lazyPage(() => import('@claritio/frontend/pages/ProcessIntelligence/ProcessList'))
const ProcessDetail = lazyPage(() => import('@claritio/frontend/pages/ProcessIntelligence/ProcessDetail'))
const SponsorProcesses = lazyPage(() => import('@claritio/frontend/pages/ProcessIntelligence/sponsor/SponsorProcesses'))
const SponsorProcessDetail = lazyPage(() => import('@claritio/frontend/pages/ProcessIntelligence/sponsor/SponsorProcessDetail'))
const SponsorReports = lazyPage(() => import('@claritio/frontend/pages/ProcessIntelligence/sponsor/SponsorReports'))
const ProcessReport = lazyPage(() => import('@claritio/frontend/pages/ProcessIntelligence/ProcessReport'))
const AssessmentNew = lazyPage(() => import('@claritio/frontend/pages/AssessmentNew'))
const AssessmentShow = lazyPage(() => import('@claritio/frontend/pages/AssessmentShow'))
const AssessmentSetup = lazyPage(() => import('@claritio/frontend/pages/AssessmentSetup'))
const SeedBundlesList = lazyPage(() => import('@claritio/frontend/pages/SeedBundles/List'))
const SeedBundlesNew = lazyPage(() => import('@claritio/frontend/pages/SeedBundles/New'))
const SeedBundlesShow = lazyPage(() => import('@claritio/frontend/pages/SeedBundles/Show'))
const SeedBundlesChat = lazyPage(() => import('@claritio/frontend/pages/SeedBundles/Chat'))
const ChatPage = lazyPage(() => import('@claritio/frontend/pages/Chat/ChatPage'))
const SeedBundlesClientPreview = lazyPage(() => import('@claritio/frontend/pages/SeedBundles/ClientPreview'))
const SeedBundlesDelta = lazyPage(() => import('@claritio/frontend/pages/SeedBundles/Delta'))
const BlueprintsIndex = lazyPage(() => import('@claritio/frontend/pages/Blueprints/BlueprintsIndex'))
const BlueprintShow = lazyPage(() => import('@claritio/frontend/pages/Blueprints/BlueprintShow'))
const AiGatewayDashboard = lazyPage(() => import('@claritio/frontend/pages/AiGateway/Dashboard'))
const AiGatewaySettings = lazyPage(() => import('@claritio/frontend/pages/AiGateway/Settings'))
const OrgAiUsage = lazyPage(() => import('@claritio/frontend/pages/AiGateway/OrgUsage'))
const BusinessCase = lazyPage(() => import('@claritio/frontend/pages/BusinessCase'))
const InitiativeList = lazyPage(() => import('@claritio/frontend/pages/Initiatives/InitiativeList'))
const InitiativeDetail = lazyPage(() => import('@claritio/frontend/pages/Initiatives/InitiativeDetail'))
const ConnectAiToolsPage = lazyPage(() => import('@claritio/frontend/pages/AiTools/ConnectAiToolsPage'))
const GatewayKeyCoverage = lazyPage(() => import('@claritio/frontend/pages/AiGateway/KeyCoverage'))
const AiGatewayUsage = lazyPage(() => import('@claritio/frontend/pages/AiGateway/Usage'))
const AiGatewayEmbeddings = lazyPage(() => import('@claritio/frontend/pages/AiGateway/Embeddings'))
const RespondentDetail = lazyPage(() => import('@claritio/frontend/pages/RespondentDetail'))
const Topics = lazyPage(() => import('@claritio/frontend/pages/Topics'))
const BlueprintCoveragePage = lazyPage(() => import('@claritio/frontend/pages/BlueprintCoverage'))
const ReportView = lazyPage(() => import('@claritio/frontend/pages/ReportView'))
const ClientReport = lazyPage(() => import('@claritio/frontend/pages/ClientReport'))
const SlidesView = lazyPage(() => import('@claritio/frontend/pages/SlidesView'))
const SlideDeckEditor = lazyPage(() => import('@claritio/frontend/pages/SlideDeckEditor'))
const Results = lazyPage(() => import('@claritio/frontend/pages/Results'))
const ResearchAgent = lazyPage(() => import('@claritio/frontend/pages/ResearchAgent'))
const InterviewLibrary = lazyPage(() => import('@claritio/frontend/pages/InterviewLibrary'))
const InterviewLibraryHub = lazyPage(() => import('@claritio/frontend/pages/InterviewLibraryHub'))
const PlaybookLibrary = lazyPage(() => import('@claritio/frontend/pages/PlaybookLibrary'))
const PlaybookDetail = lazyPage(() => import('@claritio/frontend/pages/PlaybookDetail'))
const OrganizationProfile = lazyPage(() => import('@claritio/frontend/pages/OrganizationProfile'))
const OrganizationProfileSection = lazyPage(() => import('@claritio/frontend/pages/OrganizationProfileSection'))
const EmployeeLogin = lazyPage(() => import('@claritio/frontend/pages/interview/EmployeeLogin'))
const EmployeeSignup = lazyPage(() => import('@claritio/frontend/pages/interview/EmployeeSignup'))
const EmployeeDashboard = lazyPage(() => import('@claritio/frontend/pages/interview/EmployeeDashboard'))
const MyResults = lazyPage(() => import('@claritio/frontend/pages/interview/MyResults'))
const EmployeeReport = lazyPage(() => import('@claritio/frontend/pages/interview/EmployeeReport'))
const Tier2Status = lazyPage(() => import('@claritio/frontend/pages/Tier2/Tier2Status'))
const EvalConsole = lazyPage(() => import('@claritio/frontend/pages/SeedBundles/EvalConsole'))
const StyleGuide = lazyPage(() => import('@claritio/frontend/pages/StyleGuide'))
const MeetingsSettings = lazyPage(() => import('@claritio/frontend/pages/Meetings/Settings'))
const MeetingsList = lazyPage(() => import('@claritio/frontend/pages/Meetings/List'))
const MeetingsCalendars = lazyPage(() => import('@claritio/frontend/pages/Meetings/Calendars'))
const MeetingsAudit = lazyPage(() => import('@claritio/frontend/pages/Meetings/Audit'))
const MeetingDetailPage = lazyPage(() => import('@claritio/frontend/pages/Meetings/Detail'))

// ENG-113 + ENG-170 final phase. ai-assessment's answer to "no ?org_id= in the
// URL": send the user to the client picker, which is the route whose job is to
// produce one. The shared guard takes the fallback as a required prop precisely
// so this answer stays visible rather than becoming a default — second-brain
// (ENG-202) answers differently and must not inherit this redirect.
function LegacyLibraryRedirect() {
  // ENG-97: the library moved under /interviews so the sidebar item's active
  // state matches what the user clicked; old links keep working — including
  // their ?session/?turn deep-link params.
  const { id } = useParams()
  const { search } = useLocation()
  return <Navigate to={`/interviews/${id}${search}`} replace />
}

// Keeps ?org_id= (and anything else) on the old /gateway-token bookmarks.
function PreserveSearchRedirect({ to }: { to: string }) {
  const { search } = useLocation()
  return <Navigate to={`${to}${search}`} replace />
}

export function RequireOrgOrClients() {
  return <RequireOrg fallback={<Navigate to="/clients" replace />} />
}

function DefaultRedirect() {
  return <Navigate to={landingPath()} replace />
}

export default function App({ basename }: { basename: string }) {
  useEffect(() => { document.title = BRAND.productName }, [])
  return (
    // basename = `/${locale}` (ENG-111): every absolute link/navigate below
    // resolves under the /en|/pl prefix automatically — no per-link changes.
    <BrowserRouter basename={basename}>
      <AuthProvider>
        {/* Language switch on every page outside the admin chrome (auth, employee,
            interview). AppShell renders its own toggle in the header. ENG-111. */}
        <FloatingLanguageToggle />
        <AppRoutes />
      </AuthProvider>
    </BrowserRouter>
  )
}

const routeTree = (
  <>
          {/* Public */}
          <Route path="/login" element={<Login />} />
          <Route element={<RequireAuth />}>
            <Route path="/no-access" element={<NoAccess />} />
          </Route>
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          {/* ENG-204 — receives the OAuth round-trip result: ?return_to= (success)
              or ?error= (failure). Public: the backend already set the cookie. */}
          <Route path="/oauth/callback" element={<OAuthCallback />} />
          <Route element={<TokenInterviewLayout />}>
            {/* ENG-133: the interview flow now lives inside the respondent shell. */}
            <Route element={<RespondentShell />}>
              <Route path="/i/:token" element={<InterviewWelcome />} />
              <Route path="/i/:token/chat" element={<InterviewChat />} />
              <Route path="/i/:token/done" element={<InterviewDone />} />
              {/* ENG-169: post-assessment feedback survey, same token. */}
              <Route path="/i/:token/survey" element={<SurveyForm />} />
              <Route path="/i/:token/survey/thanks" element={<SurveyThanks />} />
            </Route>
          </Route>
          <Route path="/r/:token" element={<PublicReport />} />
          <Route path="/s/:token" element={<PublicStatus />} />
          <Route path="/style-guide/*" element={<StyleGuide />} />
          {/* ENG-102: single-question follow-up reply. Deliberately outside
              TokenInterviewLayout — that layout resolves an interview invite
              token, and this page carries a follow-up token instead. */}
          <Route path="/f/:token" element={<FollowUpReply />} />
          <Route path="/employee/login" element={<EmployeeLogin />} />
          <Route path="/employee/signup" element={<EmployeeSignup />} />

          {/* Authenticated employee */}
          <Route element={<RequireEmployee />}>
            <Route path="/employee/dashboard" element={<EmployeeDashboard />} />
            <Route path="/my-results" element={<MyResults />} />
            {/* ENG-73: scoped client report — target of the shell's Raport tab */}
            <Route path="/report" element={<EmployeeReport />} />
            <Route element={<RequireSponsorProcesses />}>
              <Route path="/employee/processes" element={<SponsorProcesses />} />
              <Route path="/employee/processes/:id" element={<SponsorProcessDetail />} />
              <Route path="/employee/process-reports" element={<SponsorReports />} />
            </Route>
          </Route>

          {/* Any signed-in invitee, admins included; the API scopes the invite. */}
          <Route element={<RequireAuth loginPath="/employee/login" />}>
            <Route element={<AssessmentInterviewLayout />}>
              <Route element={<RespondentShell />}>
                <Route path="/assessment/:assessmentId/interview" element={<InterviewWelcome />} />
                <Route path="/assessment/:assessmentId/interview/chat" element={<InterviewChat />} />
                <Route path="/assessment/:assessmentId/interview/done" element={<InterviewDone />} />
              </Route>
            </Route>
          </Route>

          {/* Authenticated admin */}
          <Route element={<RequireAdmin />}>
            <Route element={<AppShell />}>
              {/* All operators (admin + consultant). Scope is enforced
                  server-side; there is no forced company-profile onboarding (ENG-47). */}
              <Route path="/clients" element={<Clients />} />
              <Route path="/clients/:id" element={<ClientPanel />} />
              {/* ENG-97: the library is per-client; the bare nav entry is a
                  client picker that skips itself when there is only one client. */}
              <Route path="/interviews" element={<InterviewLibraryHub />} />
              <Route path="/interviews/:id" element={<InterviewLibrary />} />
              <Route path="/clients/:id/interviews" element={<LegacyLibraryRedirect />} />
              {/* ENG-139: org-less portfolio Home — aggregates across every client,
                  so it deliberately sits outside RequireOrg. The old client
                  dashboard that lived at /home is now /overview (below); Home
                  itself redirects legacy /home?org_id= links there. */}
              <Route path="/home" element={<Home />} />
              <Route path="/mcp-usage" element={<McpUsagePage />} />
              <Route path="/" element={<DefaultRedirect />} />

              {/* /profile is user-scoped (PATCH /api/v1/profile) — deliberately
                  outside RequireOrg despite the ENG-113 DoD listing it, or Settings
                  becomes unreachable from /clients (no org_id → bounce loop). */}
              <Route path="/profile" element={<ProfileSettings />} />

              {/* ENG-113: every route below requires ?org_id= explicitly in the
                  URL (RequireOrgOrClients redirects to /clients otherwise) — no
                  hidden impersonation state, so a refresh or shared link can't
                  silently switch which client's data is shown. */}
              <Route element={<RequireOrgOrClients />}>
                <Route path="/overview" element={<Overview />} />
                <Route path="/processes" element={<ProcessList />} />
                <Route path="/processes/:id" element={<ProcessDetail />} />
                <Route path="/initiatives" element={<InitiativeList />} />
                <Route path="/initiatives/:id" element={<InitiativeDetail />} />
                {/* Variant C nav: screens that exist in the navigation before they are built. */}
                <Route path="/soon/:key" element={<SoonPage />} />
                <Route path="/assessments/:id/process-report" element={<ProcessReport />} />
                <Route path="/assessments" element={<AssessmentsList />} />
                <Route path="/assessments/new" element={<AssessmentNew />} />
                <Route path="/assessments/:id/setup" element={<AssessmentSetup />} />
                <Route path="/assessments/:id" element={<AssessmentShow />} />
                <Route path="/assessments/:id/respondents/:respondentId" element={<RespondentDetail />} />
                <Route path="/assessments/:id/topics" element={<Topics />} />
                <Route path="/assessments/:id/blueprint" element={<BlueprintCoveragePage />} />
                <Route path="/assessments/:id/report" element={<ReportView />} />
                <Route path="/assessments/:id/report/preview" element={<ClientReport />} />
                <Route path="/assessments/:id/report/slides" element={<SlidesView />} />
                <Route path="/assessments/:id/report/slides/edit" element={<SlideDeckEditor />} />
                <Route path="/assessments/:id/results" element={<Results />} />
                <Route path="/assessments/:id/research" element={<ResearchAgent />} />
                <Route path="/playbooks" element={<PlaybookLibrary />} />
                <Route path="/playbooks/:id" element={<PlaybookDetail />} />
                <Route path="/organization-profile" element={<OrganizationProfile />} />
                <Route path="/organization-profile/:section" element={<OrganizationProfileSection />} />
                {/* ENG-212 — an org's own AI usage. Inside RequireOrgOrClients
                    (needs ?org_id=) and NOT inside RequirePlatformAdmin: the
                    backend gates on org membership, so any member of the org
                    may see it. The platform-admin /ai-gateway route below is a
                    different data set and stays exactly as it was. */}
                <Route path="/ai-usage" element={<OrgAiUsage />} />
                <Route path="/business-case" element={<BusinessCase />} />
                <Route path="/ai-tools" element={<ConnectAiToolsPage />} />
                <Route path="/skills" element={<SkillsPage />} />
                <Route path="/gateway-token" element={<PreserveSearchRedirect to="/ai-tools" />} />
              </Route>
              {/* Second Brain: open to every operator (platform admins and
                  consultants), matching second-brain's own backend gate
                  (Api::V1::ContextBundlesController's authorize_consultant!,
                  not a platform-admin check). The ENG-202 §4 v0 restriction to
                  RequirePlatformAdmin was stricter than the backend ever was;
                  RequireOrgOrClients above already handles the org-context
                  requirement the same way every other operator-area resource
                  does. Unlike the ENG-185 AI Gateway route below, there is no
                  backend admin-only check here to stay in sync with. */}
              <Route element={<RequireOrgOrClients />}>
                <Route path="/second-brain" element={<SeedBundlesList />} />
                <Route path="/second-brain/new" element={<SeedBundlesNew />} />
                <Route path="/second-brain/:id" element={<SeedBundlesShow />} />
                <Route path="/second-brain/:id/chat" element={<SeedBundlesChat />} />
                {/* ENG-184 — preview-only surface for the eventual real
                    client chat entry point. */}
                <Route path="/second-brain/:id/client-preview" element={<SeedBundlesClientPreview />} />
                {/* ENG-240 — the week 5 demo: one question, grounded vs
                    ungrounded, side by side. */}
                <Route path="/second-brain/:id/delta" element={<SeedBundlesDelta />} />
                {/* ENG-322 (Phase 8) — Tier 2 Google Drive retrieval PoC status
                    page. Same gate as the rest of Second Brain above: org-
                    scoped consultant access, matching this app's own backend
                    (Api::V1::ProtectedController#authorize_consultant! on
                    every Tier 2 controller), not the platform-admin-only
                    AI Gateway dashboard below. */}
                <Route path="/tier2" element={<Tier2Status />} />
                {/* ENG-326 D4 — the Tier 1 eval console, promoted out of
                    SeedBundles/Show.tsx to its own route. Same gate as the
                    rest of Second Brain above; resolves the org's one bundle
                    itself, same as /tier2 and /chat, instead of taking a
                    bundle id in the URL. */}
                <Route path="/evals" element={<EvalConsole />} />
                {/* ENG-325 — the standalone chat entry point, promoted out of
                    /second-brain/:id/chat: resolves the org's one bundle
                    itself rather than requiring a bundle id in the URL. Same
                    gate as the rest of Second Brain above. */}
                <Route path="/chat" element={<ChatPage />} />
              </Route>
              {/* ENG-185 — Claritio's own internal LLM telemetry, platform zone
                  (no org_id, no RequireOrgOrClients — see lib/dashboard.ts's
                  ai-gateway entry comment for why). Gated the same way as
                  everything else that's platform-admin-only: a non-admin
                  sees ComingSoon, matching what they'd get from the backend
                  anyway (Api::V1::GatewayController requires admin). */}
              <Route element={<RequirePlatformAdmin fallback={<NoAccessPage />} />}>
                <Route path="/ai-gateway" element={<AiGatewayDashboard />} />
                <Route path="/ai-gateway/settings" element={<AiGatewaySettings />} />
                {/* ENG-245 — ops sub-page, not its own nav row; reached via a
                    link from the dashboard, same treatment as /settings. */}
                <Route path="/ai-gateway/key-coverage" element={<GatewayKeyCoverage />} />
                {/* ENG-186 — usage/cost showcase view, same sub-page
                    treatment as settings/key-coverage above. */}
                <Route path="/ai-gateway/usage" element={<AiGatewayUsage />} />
                {/* Task 9 (ENG-322 §12) — embeddings-only ops view, same
                    sub-page treatment as usage/settings/key-coverage above.
                    Claritio-staff-only by construction: this stays inside
                    RequirePlatformAdmin and nothing under /ai-usage (the
                    org-scoped surface) grows an equivalent. */}
                <Route path="/ai-gateway/embeddings" element={<AiGatewayEmbeddings />} />
              </Route>
              <Route element={<RequirePlatformAdmin fallback={<NoAccessPage />} />}>
                <Route element={<RequireOrgOrClients />}>
                  <Route path="/meetings" element={<MeetingsList />} />
                  <Route path="/meetings/settings" element={<MeetingsSettings />} />
                  <Route path="/meetings/calendars" element={<MeetingsCalendars />} />
                  <Route path="/meetings/audit" element={<MeetingsAudit />} />
                  <Route path="/meetings/:id" element={<MeetingDetailPage />} />
                </Route>
              </Route>
              <Route element={<RequirePlatformAdmin fallback={<NoAccessPage />} />}>
                <Route path="/blueprints" element={<BlueprintsIndex />} />
                <Route path="/blueprints/:id" element={<BlueprintShow />} />
              </Route>
              {/* Modules not yet built (sidebar placeholders) */}
              <Route path="/reports" element={<ComingSoon titleKey="nav.reports" />} />
              <Route path="/templates" element={<ComingSoon titleKey="nav.templates" />} />
              <Route path="/alerts" element={<ComingSoon titleKey="nav.alerts" />} />
              <Route path="/help" element={<ComingSoon titleKey="nav.help" />} />
            </Route>
          </Route>
          <Route path="*" element={<Navigate to="/" replace />} />
  </>
)

registerRoutes(createRoutesFromElements(routeTree))

// Split out from App() so tests can exercise the real route table (ENG-326
// D4 fix-round) under a MemoryRouter + AuthProvider, without also having to
// boot BrowserRouter — the router App() wraps this in only matters at
// runtime, not for asserting which component a path resolves to.
export function AppRoutes() {
  return (
    <PageBoundary fallback={<BootScreen />}>
      <Routes>{routeTree}</Routes>
    </PageBoundary>
  )
}
